import { Box, Divider, IconButton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { INotification } from 'types/notification'
import PreviewIcon from '@mui/icons-material/Preview'
import DeleteIcon from '@mui/icons-material/Delete'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'
import { useDeleteNotificationMutation } from 'services/notification'
import { toast } from 'react-toastify'
import { useEffect } from 'react'
import { DialogNotificationPreview } from './dialog-notification-preview'

type Props = {
  notification: INotification
  canEdit: boolean
}

export const NotificationCard = ({ notification, canEdit }: Props) => {
  const { t } = useTranslation()

  const dialogDeleteNotification = useBoolean(false)
  const dialogNotificationPreview = useBoolean(false)

  const [deleteNotification, { isSuccess, isError }] =
    useDeleteNotificationMutation()

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }

    if (isError) {
      toast.success(t('operation-failure'))
    }
  }, [isSuccess, isError, t])

  return (
    <>
      <ConfirmationDialog
        open={dialogDeleteNotification.isTrue}
        onClose={dialogDeleteNotification.setFalse}
        onConfirm={() => deleteNotification({ id: notification.id })}
        text={t('are-you-sure-you-want-to-delete')}
      />

      {dialogNotificationPreview.isTrue && (
        <DialogNotificationPreview
          notification={notification}
          onClose={dialogNotificationPreview.setFalse}
        />
      )}
      <Box
        bgcolor="#FFFFFF"
        padding={2}
        borderRadius={1}
        marginTop={2}
        borderLeft={4}
        borderColor="primary.main"
        boxShadow={2}
      >
        <Stack direction="row" spacing={1}>
          <Stack minWidth={0} flexGrow={1} spacing={2}>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {t('organization')}
                </Typography>
                <Typography variant="body2">
                  {notification.organizationName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {t('fullName')}
                </Typography>
                <Typography variant="body2">{notification.personalName}</Typography>
              </Box>

              <Stack>
                <Typography variant="body2" fontWeight="bold">
                  {t('creationDate')}
                </Typography>
                <Typography variant="body2">
                  {formatTimeStampToDate(notification.createTime as string)}
                </Typography>
              </Stack>
            </Stack>

            <Stack>
              <Typography variant="body2" fontWeight="bold">
                {t('description')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {notification.messageBody}
              </Typography>
            </Stack>
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Stack justifyContent="center" spacing={3} flexShrink={0}>
            <IconButton
              onClick={dialogNotificationPreview.setTrue}
              sx={{ width: 40 }}
            >
              <PreviewIcon />
            </IconButton>
            {canEdit && (
              <IconButton
                onClick={dialogDeleteNotification.setTrue}
                sx={{ width: 40 }}
              >
                <DeleteIcon color="error" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Box>
    </>
  )
}
